import { untrack } from 'svelte'

import type { PushConfig } from '../api/types'
import {
  isNativeShell,
  nativeHereAlerts,
  nativeHereState,
  nativePushEnable,
  nativePushState,
  type NativeHere,
  type NativePush,
} from '../native'
import { isIos, isStandalone } from '../pwa.svelte'
import { read, readJson, remove, write } from '../storage'
import { whileVisible } from '../visibility'
import { alertPlaces, places, setPlaceAlerts } from './places.svelte'

/**
 * Alerts at saved places, the phone's half (Phase 9 plan §9). The server's
 * half is `app/push/`; this module is what it holds a copy of.
 *
 * **The phone is the source of truth and the server's copy is a cache.** What
 * the server keeps — the subscription, the places whose bell is on, the level
 * — is PUT whole on every launch and after every change, so a server that
 * lost its files, or a key that was regenerated, heals the next time the app
 * is opened rather than never (§9.4). A PUT that changes nothing is skipped
 * for a day, so an ordinary launch costs no request.
 *
 * **Nothing asks for notification permission except a bell being tapped.**
 * iOS grants the prompt only inside a user gesture, and a prompt at boot is
 * the one every browser now punishes. `enableAlerts` calls
 * `Notification.requestPermission()` before its first `await` for that reason,
 * and the service worker's registration is fetched ahead of time so
 * `subscribe` does not wait on it either.
 *
 * **In the iOS app the transport is APNs** (ios-app-plan §5.4, §6.2): there
 * is no service worker and no Push API in a WKWebView, so the app hands over
 * a device token and the PUT carries that instead of a Web Push
 * subscription. Everything else here — the levels, the rain switch, the day
 * gate, the state line — is the same code for both.
 *
 * **Alerts where I am is the app's alone** (ios-app-plan §6.4): a switch that
 * keeps the subscription alive with no bell on, while the app itself reports
 * where the phone is to `/api/push/here`, from the background. The page only
 * reads that state (`push.here`) and flips the switch; it never sends a
 * position, because it is not running when the position matters.
 */

export type AlertLevel = 'warnings' | 'watches' | 'all'

/** The server's `LEVELS` (`app/push/poller.py`), in the same order. */
export const ALERT_LEVELS: readonly { id: AlertLevel; label: string }[] = [
  { id: 'warnings', label: 'Warnings only' },
  { id: 'watches', label: 'Warnings and watches' },
  { id: 'all', label: 'Everything' },
]
export const DEFAULT_LEVEL: AlertLevel = 'watches'

const KEY_LEVEL = 'weather.alertLevel'
/** Rain starting soon, at every place whose bell is on (nowcast Stage C). */
const KEY_RAIN = 'weather.rainAlerts'
/** What this device last told the server, and the key it subscribed with. */
const KEY_SYNC = 'weather.push'

/** An unchanged PUT is repeated once a day, so a server that lost it heals. */
export const RESYNC_MS = 24 * 3600_000
/** How often an open app looks again; the day gate above keeps it free. */
const CHECK_MS = 60 * 60_000

interface Synced {
  /** The `key_id` the browser's subscription was made against. */
  keyId: string | null
  /** In the app: the APNs token the server was last given, so a new one can
   *  replace it and the last bell off can delete it. */
  token: string | null
  /** A hash of the last PUT body the server accepted. */
  sent: string | null
  at: number
}

/**
 * - `unknown`: `/api/config` has not answered.
 * - `server-off`: the server has no push, so nothing is offered.
 * - `install`: iOS in a Safari tab, where push exists only for an installed app.
 * - `unsupported`: no service worker, no `PushManager` or no `Notification`.
 * - `ready`: the bells can work.
 */
export type PushSupport = 'unknown' | 'server-off' | 'install' | 'unsupported' | 'ready'

export const push = $state({
  config: undefined as PushConfig | null | undefined,
  level: DEFAULT_LEVEL as AlertLevel,
  /** Also tell me when rain is about to start at those places. */
  rain: false,
  permission: 'default' as NotificationPermission,
  /** The browser holds a subscription for this origin. */
  subscribed: false,
  /** The server has what this device last meant to send it. */
  synced: false,
  /** A bell or the test button is working. */
  busy: false,
  /** What went wrong, in words, for the state line; null when nothing did. */
  problem: null as string | null,
  /** What the last "Send a test notification" did. */
  test: null as string | null,
  /** Alerts where I am, as the app reports it; null outside the app. */
  here: null as NativeHere | null,
})

/** The switch is on: the subscription stays with no bell on. */
function hereOn(): boolean {
  return isNativeShell() && push.here?.on === true
}

let registration: ServiceWorkerRegistration | null = null

export function pushSupport(): PushSupport {
  if (push.config === undefined) return 'unknown'
  if (push.config === null) return 'server-off'
  // The app is installed by definition, and needs the server's APNs instead
  // of anything in the web view.
  if (isNativeShell()) return push.config.apns ? 'ready' : 'unsupported'
  if (isIos() && !isStandalone()) return 'install'
  if (
    typeof navigator === 'undefined' ||
    !('serviceWorker' in navigator) ||
    typeof window === 'undefined' ||
    !('PushManager' in window) ||
    !('Notification' in window)
  ) {
    return 'unsupported'
  }
  return 'ready'
}

/** Read once at boot, from `loadPrefs()`, like every other preference. */
export function loadPushPrefs(): void {
  const level = read(KEY_LEVEL)
  push.level = ALERT_LEVELS.some((l) => l.id === level) ? (level as AlertLevel) : DEFAULT_LEVEL
  push.rain = read(KEY_RAIN) === '1'
}

export function setLevel(level: AlertLevel): void {
  if (!ALERT_LEVELS.some((l) => l.id === level)) return
  push.level = level
  if (level === DEFAULT_LEVEL) remove(KEY_LEVEL)
  else write(KEY_LEVEL, level)
}

/**
 * The rain switch. No permission prompt of its own: it adds to the places a
 * bell is already on at, and the bell is what asked.
 */
export function setRain(on: boolean): void {
  push.rain = on
  if (on) write(KEY_RAIN, '1')
  else remove(KEY_RAIN)
}

/** `/api/config`'s answer. The sync effect runs on it. */
export function setPushConfig(config: PushConfig | null | undefined): void {
  push.config = config
  if (pushSupport() !== 'ready') return
  if (isNativeShell()) {
    void nativePushState().then((state) => {
      if (state) push.permission = state.permission
    })
    void refreshHere()
    return
  }
  push.permission = Notification.permission
  // Fetched now so a bell's click handler has it without an await: iOS
  // allows `subscribe` only while the tap's activation lasts.
  void navigator.serviceWorker.ready.then((r) => (registration = r))
}

function readSynced(): Synced {
  const raw = readJson<Partial<Synced>>(KEY_SYNC)
  return {
    keyId: typeof raw?.keyId === 'string' ? raw.keyId : null,
    token: typeof raw?.token === 'string' ? raw.token : null,
    sent: typeof raw?.sent === 'string' ? raw.sent : null,
    at: typeof raw?.at === 'number' ? raw.at : 0,
  }
}

function writeSynced(value: Synced): void {
  write(KEY_SYNC, JSON.stringify(value))
}

async function currentRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (registration) return registration
  try {
    registration = (await navigator.serviceWorker.getRegistration()) ?? null
  } catch {
    registration = null
  }
  return registration
}

function keyBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const b64 = base64url.replace(/-/g, '+').replace(/_/g, '/')
  const binary = atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4))
  const out = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i)
  return out
}

/**
 * Whether `sub` was made against the server's current key. The subscription
 * says so itself where the browser exposes `options.applicationServerKey`
 * (all three engines do); the stored `key_id` is the fallback. A mismatch is
 * a key the server no longer has, and every push to it would fail (§8.2).
 */
function madeWith(sub: PushSubscription, config: PushConfig): boolean {
  const key = sub.options?.applicationServerKey
  if (key && key.byteLength) {
    const have = new Uint8Array(key)
    const want = keyBytes(config.public_key)
    return have.length === want.length && have.every((b, i) => b === want[i])
  }
  return readSynced().keyId === config.key_id
}

function describeSubscribeError(e: unknown): string {
  const name = e instanceof DOMException ? e.name : ''
  if (name === 'NotAllowedError') return "This browser's settings block notifications from this site"
  if (name === 'AbortError' || name === 'NotSupportedError') {
    // Brave ships with its push service off: `subscribe` fails with
    // "push service error" before our server is involved (plan §8.10).
    if ('brave' in navigator) {
      return 'Brave has push turned off. Turn on "Use Google services for push messaging" in Brave\'s privacy settings'
    }
    return "This browser's push service would not subscribe"
  }
  return "This browser couldn't subscribe"
}

/**
 * A subscription made against the current key: the one the browser holds, or
 * a new one. Null, with `push.problem` set, when there is none to be had.
 */
async function ensureSubscription(config: PushConfig): Promise<PushSubscription | null> {
  const reg = await currentRegistration()
  if (!reg) {
    push.problem = 'The app has not finished installing its offline support yet. Try again in a moment'
    return null
  }
  let sub = await reg.pushManager.getSubscription()
  if (sub && !madeWith(sub, config)) {
    await forget(sub)
    sub = null
  }
  if (!sub) {
    try {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: keyBytes(config.public_key),
      })
    } catch (e) {
      console.warn('push subscribe failed', e)
      push.problem = describeSubscribeError(e)
      push.subscribed = false
      return null
    }
    writeSynced({ keyId: config.key_id, token: null, sent: null, at: 0 })
  }
  push.subscribed = true
  return sub
}

/** Unsubscribe here and tell the server. The server's answer is not needed. */
async function forget(sub: PushSubscription): Promise<void> {
  try {
    await fetch('/api/push/subscription', {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ endpoint: sub.endpoint }),
    })
  } catch {
    // Unreachable. A push to an unsubscribed endpoint is a 410, and the
    // worker deletes the file on that.
  }
  try {
    await sub.unsubscribe()
  } catch {
    // Already gone.
  }
  remove(KEY_SYNC)
  push.subscribed = false
}

/** FNV-1a: enough to tell one PUT body from another, and short to store. */
function hash(text: string): string {
  let h = 0x811c9dc5
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return (h >>> 0).toString(16).padStart(8, '0')
}

let running: Promise<void> | null = null
let again = false
let againForced = false

/**
 * Bring the server's copy in line with this device (§9.4). One at a time: a
 * call while one runs queues exactly one more, which reads the state as it is
 * by then.
 */
export function syncPush(options: { force?: boolean } = {}): Promise<void> {
  if (running) {
    again = true
    againForced ||= !!options.force
    return running
  }
  running = (async () => {
    try {
      await syncOnce(!!options.force)
      while (again) {
        const force = againForced
        again = againForced = false
        await syncOnce(force)
      }
    } finally {
      running = null
    }
  })()
  return running
}

async function syncOnce(force: boolean, retried = false): Promise<void> {
  const config = push.config
  if (pushSupport() !== 'ready' || !config) return
  if (isNativeShell()) return syncNative(force)
  push.permission = Notification.permission
  const reg = await currentRegistration()
  if (!reg) return
  const wanted = alertPlaces()
  const held = await reg.pushManager.getSubscription()

  if (wanted.length === 0) {
    // The last bell went off here, or on a build that did not tell the server.
    if (held) await forget(held)
    push.subscribed = false
    push.synced = true
    push.problem = null
    return
  }
  if (push.permission !== 'granted') {
    // Blocked since the bells were turned on. Nothing can arrive, and asking
    // again needs a tap; the state line says so.
    push.subscribed = !!held
    push.synced = false
    return
  }

  const sub = await ensureSubscription(config)
  if (!sub) {
    push.synced = false
    return
  }
  const body = JSON.stringify({
    subscription: sub.toJSON(),
    key_id: config.key_id,
    level: push.level,
    rain: push.rain,
    places: wanted.map(({ id, name, lat, lon }) => ({ id, name, lat, lon })),
  })
  const print = hash(body)
  const last = readSynced()
  if (!force && last.sent === print && Date.now() - last.at < RESYNC_MS) {
    push.synced = true
    push.problem = null
    return
  }

  let resp: Response
  try {
    resp = await fetch('/api/push/subscription', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body,
    })
  } catch {
    push.synced = false
    push.problem = "Can't reach the server; it will be told when the app is next open"
    return
  }
  if (resp.status === 409 && !retried) {
    // The server's key is not the one `/api/config` gave us: it changed under
    // us. Ask again, then subscribe against the new one.
    push.config = await refetchConfig()
    return syncOnce(true, true)
  }
  if (!resp.ok) {
    push.synced = false
    push.problem = `The server did not take it (HTTP ${resp.status})`
    return
  }
  writeSynced({ keyId: config.key_id, token: null, sent: print, at: Date.now() })
  push.synced = true
  push.problem = null
}

/** Tell the server to forget an APNs token. Its answer is not needed: a
 *  token the app no longer has is `Unregistered`, and the worker deletes the
 *  file on that. */
async function forgetToken(token: string): Promise<void> {
  try {
    await fetch('/api/push/subscription', {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    })
  } catch {
    // Unreachable.
  }
}

function noToken(): string {
  return "iOS hasn't given the app a notification token yet. Try again in a moment"
}

/**
 * `syncOnce` in the iOS app: the same steps with an APNs token in place of a
 * Web Push subscription. The token comes from the app on every launch (iOS
 * may change it), so a token that differs from the one the server was given
 * replaces it there.
 */
async function syncNative(force: boolean): Promise<void> {
  const state = await nativePushState()
  if (!state) return
  push.permission = state.permission
  const wanted = alertPlaces()
  const last = readSynced()

  if (wanted.length === 0 && !hereOn()) {
    if (last.token) await forgetToken(last.token)
    if (last.token || last.sent) remove(KEY_SYNC)
    push.subscribed = false
    push.synced = true
    push.problem = null
    return
  }
  if (state.permission !== 'granted') {
    push.subscribed = !!last.token
    push.synced = false
    return
  }
  if (!state.token || !state.env) {
    push.subscribed = false
    push.synced = false
    push.problem = noToken()
    return
  }
  if (last.token && last.token !== state.token) await forgetToken(last.token)
  push.subscribed = true

  const body = JSON.stringify({
    subscription: { kind: 'apns', token: state.token, env: state.env },
    level: push.level,
    rain: push.rain,
    places: wanted.map(({ id, name, lat, lon }) => ({ id, name, lat, lon })),
  })
  const print = hash(body)
  if (!force && last.sent === print && Date.now() - last.at < RESYNC_MS) {
    push.synced = true
    push.problem = null
    return
  }
  let resp: Response
  try {
    resp = await fetch('/api/push/subscription', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body,
    })
  } catch {
    push.synced = false
    push.problem = "Can't reach the server; it will be told when the app is next open"
    return
  }
  if (!resp.ok) {
    push.synced = false
    push.problem =
      resp.status === 404
        ? "This server isn't set up to send to the app"
        : `The server did not take it (HTTP ${resp.status})`
    return
  }
  writeSynced({ keyId: null, token: state.token, sent: print, at: Date.now() })
  push.synced = true
  push.problem = null
}

let refetch: () => Promise<PushConfig | null | undefined> = async () => push.config

/** How to read `/api/config` again after a 409. `App` sets it. */
export function setConfigSource(source: () => Promise<PushConfig | null | undefined>): void {
  refetch = source
}

async function refetchConfig(): Promise<PushConfig | null | undefined> {
  try {
    return await refetch()
  } catch {
    return push.config
  }
}

/**
 * Turn a place's bell on. **Call this straight from the click handler**: the
 * permission prompt is the first thing it does, before any `await`, because
 * iOS grants it only inside the tap.
 */
export async function enableAlerts(placeId: string): Promise<void> {
  const config = push.config
  if (pushSupport() !== 'ready' || !config) return
  if (isNativeShell()) return enableNative(placeId, nativePushEnable())
  const asked: Promise<NotificationPermission> =
    Notification.permission === 'default'
      ? Notification.requestPermission()
      : Promise.resolve(Notification.permission)
  push.busy = true
  push.problem = null
  try {
    const permission = await asked
    push.permission = permission
    if (permission !== 'granted') {
      if (permission === 'default') push.problem = 'Notifications were not allowed'
      return
    }
    const sub = await ensureSubscription(config)
    if (!sub) return
    setPlaceAlerts(placeId, true)
    // Not forced: the sync effect has already queued this change, and a
    // second PUT of the same body would be skipped by its hash anyway.
    await syncPush()
  } finally {
    push.busy = false
  }
}

/** `enableAlerts` in the app. `asked` was started inside the tap. */
async function enableNative(placeId: string, asked: Promise<NativePush | null>): Promise<void> {
  push.busy = true
  push.problem = null
  try {
    const state = await asked
    if (!state) {
      push.problem = "The app didn't answer"
      return
    }
    push.permission = state.permission
    if (state.permission !== 'granted') {
      if (state.permission === 'default') push.problem = 'Notifications were not allowed'
      return
    }
    if (!state.token) {
      push.problem = noToken()
      return
    }
    setPlaceAlerts(placeId, true)
    await syncPush()
  } finally {
    push.busy = false
  }
}

/** Read the app's here state again: the reported time and the permission
 *  move while the page is not looking. */
export async function refreshHere(): Promise<void> {
  if (!isNativeShell()) return
  const state = await nativeHereState()
  if (state) push.here = state
}

/**
 * Turn alerts where I am on. **Call it straight from the switch's handler**:
 * the notification prompt is the first thing it does, as a bell's is. Then
 * the subscription is PUT (the app's report needs one to land on), and only
 * then is the app told, which is what asks iOS for Always.
 */
export async function enableHere(): Promise<void> {
  if (!isNativeShell() || pushSupport() !== 'ready') return
  const asked = nativePushEnable()
  push.busy = true
  push.problem = null
  try {
    const state = await asked
    if (!state) {
      push.problem = "The app didn't answer"
      return
    }
    push.permission = state.permission
    if (state.permission !== 'granted') {
      if (state.permission === 'default') push.problem = 'Notifications were not allowed'
      return
    }
    if (!state.token) {
      push.problem = noToken()
      return
    }
    push.here = { on: true, permission: push.here?.permission ?? 'prompt', reported: push.here?.reported ?? null }
    await syncPush({ force: true })
    const here = await nativeHereAlerts(true)
    if (here) push.here = here
  } finally {
    push.busy = false
  }
}

/** Turn it off: the app stops watching and deletes the server's position,
 *  and the subscription goes too if no bell is on either. */
export async function disableHere(): Promise<void> {
  if (!isNativeShell()) return
  push.busy = true
  try {
    const here = await nativeHereAlerts(false)
    push.here = here ?? { on: false, permission: push.here?.permission ?? 'prompt', reported: null }
    await syncPush()
  } finally {
    push.busy = false
  }
}

/** What the switch says under it, or null when it is off. */
export function hereLine(now = Date.now()): string | null {
  const here = push.here
  if (!here?.on) return null
  if (here.permission === 'denied' || here.permission === 'prompt') {
    return 'Location is off for Fusion WX, so nothing is reported. Turn it on in iOS Settings'
  }
  const when = here.reported === null ? 'Not reported yet' : `Last reported ${ago(now - here.reported)}`
  if (here.permission === 'whenInUse') {
    return `${when}. Set location to “Always” in iOS Settings, or this works only while the app is open`
  }
  return when
}

function ago(ms: number): string {
  const minutes = Math.max(0, Math.round(ms / 60_000))
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes} min ago`
  const hours = Math.round(minutes / 60)
  if (hours < 48) return `${hours} h ago`
  return `${Math.round(hours / 24)} days ago`
}

/** Turn a place's bell off. The last one off unsubscribes this device. */
export async function disableAlerts(placeId: string): Promise<void> {
  setPlaceAlerts(placeId, false)
  push.busy = true
  try {
    await syncPush()
  } finally {
    push.busy = false
  }
}

/**
 * "Send a test notification" (§9.5): the only way to know the whole chain
 * works — this device's subscription, the server's key, the push service and
 * the service worker — and what 9.6's exit check uses.
 */
export async function sendTest(): Promise<void> {
  push.test = null
  let device: { endpoint: string } | { token: string } | null
  if (isNativeShell()) {
    const token = readSynced().token
    device = token ? { token } : null
  } else {
    const reg = await currentRegistration()
    const sub = await reg?.pushManager.getSubscription()
    device = sub ? { endpoint: sub.endpoint } : null
  }
  if (!device) {
    push.test = isNativeShell() ? 'Turn on a bell or alerts where you are first' : 'Turn on a bell first'
    return
  }
  push.busy = true
  try {
    const resp = await fetch('/api/push/test', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(device),
    })
    if (resp.ok) push.test = 'Sent. It should arrive within a few seconds'
    else if (resp.status === 429) push.test = 'One test every 30 seconds'
    else if (resp.status === 404) {
      // The server has no copy: send it one, so the next try works.
      await syncPush({ force: true })
      push.test = 'The server had lost this device. It has it again; try once more'
    } else if (resp.status === 502) {
      const detail = (await resp.json().catch(() => null)) as { status?: number; reason?: unknown } | null
      // The service's own word for why (APNs: `BadEnvironmentKeyInToken`), shown only
      // when it looks like one, since it is a third party's string.
      const reason = typeof detail?.reason === 'string' && /^[A-Za-z]{1,64}$/.test(detail.reason) ? detail.reason : ''
      const why = [detail?.status ? `HTTP ${detail.status}` : '', reason].filter(Boolean).join(', ')
      push.test = `The push service refused it${why ? ` (${why})` : ''}`
    } else push.test = `The server could not send it (HTTP ${resp.status})`
  } catch {
    push.test = "Can't reach the server"
  } finally {
    push.busy = false
  }
}

/** The state line (§9.5), or null when the section has nothing to say. */
export function pushStateLine(): string | null {
  const support = pushSupport()
  if (support === 'unknown' || support === 'server-off') return null
  if (support === 'install') return 'Install the app to get alerts'
  // The server has Web Push but no APNs settings: nothing reaches the app.
  if (support === 'unsupported' && isNativeShell()) return "This server doesn't send notifications to the app"
  if (support === 'unsupported') return "This browser can't receive notifications"
  if (push.permission === 'denied') {
    return isNativeShell() ? 'Turned off in iOS Settings' : "Blocked in this browser's settings"
  }
  const on = places.list.filter((p) => p.alerts).length
  const here = hereOn()
  if (on === 0 && !here) return 'Off'
  const where = on === 0 ? '' : `for ${on} place${on === 1 ? '' : 's'}`
  const line = `On ${[where, here ? 'where you are' : ''].filter(Boolean).join(' and ')}`
  if (push.permission !== 'granted') return `${line}, but notifications are not allowed yet`
  return push.synced ? line : `${line} · not confirmed by the server yet`
}

/**
 * Start keeping the server in line: at once, on every change to what a PUT
 * carries, and on every return to the app (gated by the day). Returns a
 * disposer. Also listens for the service worker's word that the browser
 * replaced the subscription (`pushsubscriptionchange`).
 */
export function startPushSync(): () => void {
  const stopEffect = $effect.root(() => {
    $effect(() => {
      // Everything a PUT carries, read so a change to any of it runs this.
      void push.config
      void push.level
      void push.rain
      void push.here?.on
      for (const p of places.list) void [p.alerts, p.name, p.lat, p.lon]
      // Untracked: what the sync reads and writes along the way is not a
      // reason to run it again.
      untrack(() => void syncPush())
    })
  })
  const stopVisible = whileVisible(() => {
    void syncPush()
    void refreshHere()
  }, CHECK_MS)
  const sw = typeof navigator !== 'undefined' ? navigator.serviceWorker : undefined
  const onMessage = (e: MessageEvent) => {
    if (e.data && typeof e.data === 'object' && e.data.type === 'fusionwx:resync') {
      void syncPush({ force: true })
    }
  }
  sw?.addEventListener('message', onMessage)
  return () => {
    stopEffect()
    stopVisible()
    sw?.removeEventListener('message', onMessage)
  }
}

/** Tests only. */
export function _resetPush(): void {
  registration = null
  running = null
  again = againForced = false
  refetch = async () => push.config
  push.config = undefined
  push.level = DEFAULT_LEVEL
  push.rain = false
  push.permission = 'default'
  push.subscribed = false
  push.synced = false
  push.busy = false
  push.problem = null
  push.test = null
  push.here = null
}
